import { useCallback, useEffect, useMemo, useState } from 'react';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { Box, Chip, Typography, MenuItem, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { handlePriceWithRM } from '../../utils/priceUtils';
import { getAllClaims, regenerateAllClaims } from '../../api/incentiveClaimApi';
import { getAllCampaigns } from '../../api/incentiveApi';
import { useUser } from '../../context/userContext';
import useTableState from '../../hooks/useTableState';
import IncentiveAsternModal from './incentiveAsternModal';
import IncentivePayoutModal from './incentivePayoutModal';

const STATUS_OPTIONS = ['In Progress', 'Eligible', 'On Slip', 'Received', 'Completed', 'Void', 'Archived'];

const STATUS_CHIP = {
    'In Progress': { bg: '#FEF3C7', color: '#92400E' },
    Eligible: { bg: '#E0E7FF', color: '#3730A3' },
    'On Slip': { bg: '#CCFBF1', color: '#115E59' },
    Received: { bg: '#DBEAFE', color: '#1E3A8A' },
    Completed: { bg: '#D1FAE5', color: '#065F46' },
    Void: { bg: '#FEE2E2', color: '#991B1B' },
    Archived: { bg: '#E2E8F0', color: '#475569' },
};

const StatusChip = ({ value }) => {
    const cfg = STATUS_CHIP[value] || { bg: '#F1F5F9', color: '#64748B' };
    return (
        <Chip
            label={value || '—'}
            size="small"
            sx={{ height: 22, fontSize: '0.68rem', fontWeight: 600, fontFamily: "'Poppins', sans-serif", backgroundColor: cfg.bg, color: cfg.color }}
        />
    );
};

export default function IncentiveClaimsList() {
    const ts = useTableState('', { defaultSorting: [{ id: 'salesId', desc: true }] });
    const [data, setData] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [campaignId, setCampaignId] = useState('');
    const [status, setStatus] = useState('');
    const [loading, setLoading] = useState(false);
    const [asternRow, setAsternRow] = useState(null);
    const [payoutRow, setPayoutRow] = useState(null);
    const [caseChildren, setCaseChildren] = useState(null);
    const { user } = useUser();
    const isSuperAdmin = !!user?.roles?.includes('Super Admin');
    const [regenerating, setRegenerating] = useState(false);

    useEffect(() => {
        getAllCampaigns()
            .then(r => {
                const list = Array.isArray(r.data) ? r.data : [];
                setCampaigns(list);
                if (list.length > 0) setCampaignId(prev => prev || (list[0].campaignId ?? list[0].id));
            })
            .catch(() => setCampaigns([]));
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const r = await getAllClaims({ campaignId });
            setData(Array.isArray(r.data) ? r.data : []);
        } catch (e) {
            console.error('Failed to load incentive claims:', e);
            setData([]);
        } finally {
            setLoading(false);
        }
    }, [campaignId]);

    const visibleData = useMemo(
        () => status ? data.filter(d => (d.displayStatus ?? 'In Progress') === status) : data,
        [data, status]
    );

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleRegenerateAll = async () => {
        if (!window.confirm(
            'Regenerate ALL incentive claims from current sales + commission data?\n\n'
            + 'This only ADDS missing claims and refreshes amounts. It never deletes claims, '
            + 'never touches commissions, and never changes rows you have already marked Received/Paid/On-Slip. '
            + 'Safe to run.'
        )) return;
        setRegenerating(true);
        try {
            const r = await regenerateAllClaims();
            await fetchData();
            window.alert(`Done — processed ${r.data?.processed ?? '?'} sales. Claims refreshed.`);
        } catch (e) {
            console.error('Regenerate all failed:', e);
            window.alert('Regenerate failed: ' + (e?.response?.data || e?.message || 'unknown error'));
        } finally {
            setRegenerating(false);
        }
    };


    const columns = useMemo(() => [
        { id: 'salesId', header: 'Sales #', accessorFn: row => row.salesId, size: 80,
          Cell: ({ row }) => (row.original.salesId > 0 ? row.original.salesId : '—') },
        { id: 'customerName', header: 'Customer', accessorFn: row => row.customerName ?? '', size: 150 },
        { id: 'agentName', header: 'Agent', accessorFn: row => row.agentName ?? '', size: 150 },
        { id: 'rowType', header: 'Type', accessorKey: 'rowType', size: 130 },
        { id: 'campaignName', header: 'Campaign', accessorFn: row => row.campaignName ?? '', size: 160 },
        { id: 'metric', header: 'Metric', accessorKey: 'metric', size: 100 },
        {
            id: 'contribution', header: 'Contribution', size: 110,
            accessorFn: row => row.contribution ?? 0,
            Cell: ({ row }) => {
                const v = row.original.contribution;
                return v != null ? new Intl.NumberFormat('en-MY', { maximumFractionDigits: 2 }).format(v) : '—';
            },
        },
        {
            id: 'amount', header: 'Amount', size: 110,
            accessorFn: row => row.rowType === 'Total Incentive' ? (row.asternAmount ?? 0) : (row.rewardType === 'Cash' ? (row.amount ?? 0) : -1),
            Cell: ({ row }) => {
                const r = row.original;
                if (r.rowType === 'Total Incentive') return handlePriceWithRM(r.asternAmount ?? 0);
                return r.rewardType === 'Cash' ? handlePriceWithRM(r.amount ?? 0) : '—';
            },
        },
        {
            id: 'reward', header: 'Reward', size: 140,
            accessorFn: row => row.rewardType !== 'Cash' ? (row.rewardLabel ?? '') : '',
            Cell: ({ row }) => {
                const r = row.original;
                return r.rewardType !== 'Cash' ? (r.rewardLabel || '—') : '';
            },
        },
        {
            id: 'asternReceived', header: 'Received from Astern?', size: 150,
            accessorFn: row => row.asternReceived ? 'Yes' : 'No',
            Cell: ({ row }) => (
                <span style={{ color: row.original.asternReceived ? '#065F46' : '#94A3B8', fontWeight: 600, fontSize: '0.75rem' }}>
                    {row.original.asternReceived ? 'Yes' : 'No'}
                </span>
            ),
        },
        {
            id: 'commProgress', header: 'Comm % (Astern)', size: 120,
            accessorFn: row => row.commProgressPercent ?? -1,
            Cell: ({ row }) => {
                const r = row.original;
                if (r.rowType !== 'Total Incentive' || r.commProgressPercent == null) return '—';
                const pct = r.commProgressPercent;
                return <span style={{ color: pct >= 100 ? '#065F46' : '#92400E', fontWeight: 600, fontSize: '0.75rem' }}>{pct}%</span>;
            },
        },
        {
            id: 'status', header: 'Status', size: 110,
            accessorFn: row => row.displayStatus ?? 'In Progress',
            Cell: ({ row }) => <StatusChip value={row.original.displayStatus} />,
        },
    ], []);

    const table = useMaterialReactTable({
        columns,
        data: visibleData,
        getRowId: (row) => String(row.claimId),
        enableColumnActions: false,
        enableColumnDragging: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        enableStickyHeader: true,
        // Mobile: let vertical scroll flow to the page (no inner table scroll box).
        // Wide tables still scroll horizontally; sticky header just won't trap scroll.
        muiTableContainerProps: { sx: { maxHeight: 'unset' } },
        initialState: { density: 'compact', showGlobalFilter: true },
        state: {
            isLoading: loading,
            globalFilter: ts.globalFilter,
            sorting: ts.sorting,
            pagination: ts.pagination,
            columnFilters: ts.columnFilters,
        },
        onGlobalFilterChange: ts.setGlobalFilter,
        onSortingChange: ts.setSorting,
        onPaginationChange: ts.setPagination,
        onColumnFiltersChange: ts.setColumnFilters,
        muiTablePaperProps: { elevation: 0, sx: { border: 'none', backgroundColor: 'transparent', borderRadius: '14px' } },
        muiTableHeadCellProps: { sx: { backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.5px', color: '#64748B', fontFamily: "'Poppins', sans-serif", padding: '8px 10px' } },
        muiTableBodyCellProps: { sx: { fontSize: '0.8rem', color: '#374151', borderBottom: '1px solid #F1F5F9', fontFamily: "'Poppins', sans-serif", padding: '6px 10px' } },
        muiTableBodyRowProps: ({ row }) => {
            const r = row.original;
            const hasChildren = Array.isArray(r.children) && r.children.length > 0;
            // A Total Incentive row with a real (positive) claimId is recordable — covers
            // battery (per-sale) AND case (per-agent lump) Totals.
            const recordable = r.rowType === 'Total Incentive' && r.claimId > 0;
            const clickable = hasChildren || recordable || (r.salesId > 0 && r.rowType === 'Incentive');
            return {
                onClick: () => {
                    if (hasChildren) { setCaseChildren(r); return; }
                    if (recordable) setAsternRow(r);
                    else if (r.salesId > 0 && r.rowType === 'Incentive') setPayoutRow(r);
                },
                sx: { cursor: clickable ? 'pointer' : 'default', '&:hover': { backgroundColor: '#F8FAFC' } },
            };
        },
        muiTopToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderBottom: '1px solid #E2E8F0', minHeight: '48px' } },
        muiBottomToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderTop: '1px solid #E2E8F0' } },
        renderBottomToolbarCustomActions: ({ table }) => {
            const rows = table.getFilteredRowModel().rows.map(r => r.original);
            const fromAstern = rows.filter(r => r.rowType === 'Total Incentive').reduce((s, r) => s + (r.asternAmount || 0), 0);
            const toAgents = rows.filter(r => r.rowType === 'Incentive' && r.rewardType === 'Cash').reduce((s, r) => s + (r.amount || 0), 0);
            const webbey = rows.filter(r => r.rowType === 'Webbey Incentive').reduce((s, r) => s + (r.amount || 0), 0);
            return (
                <Typography component="div" sx={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', color: '#475569', fontFamily: "'Poppins', sans-serif", pl: '8px', flexWrap: 'wrap' }}>
                    <span>From Astern: {handlePriceWithRM(fromAstern)}</span>
                    <span>{'·'}</span>
                    <span>To Agents: {handlePriceWithRM(toAgents)}</span>
                    <span>{'·'}</span>
                    <span>Webbey: {handlePriceWithRM(webbey)}</span>
                </Typography>
            );
        },
    });

    return (
        <div>
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                    select
                    size="small"
                    label="Campaign"
                    value={campaignId}
                    onChange={(e) => setCampaignId(e.target.value)}
                    sx={{ minWidth: 200, '& .MuiInputBase-root': { fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem' } }}
                >
                    <MenuItem value="">All Campaigns</MenuItem>
                    {campaigns.map(c => {
                        const id = c.campaignId ?? c.id;
                        return (
                            <MenuItem key={id} value={id}>{c.name ?? c.campaignName ?? `#${id}`}</MenuItem>
                        );
                    })}
                </TextField>
                <TextField
                    select
                    size="small"
                    label="Status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    sx={{ minWidth: 150, '& .MuiInputBase-root': { fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem' } }}
                >
                    <MenuItem value="">All Statuses</MenuItem>
                    {STATUS_OPTIONS.map(s => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                </TextField>
                {isSuperAdmin && (
                    <Button
                        variant="outlined"
                        size="small"
                        disabled={regenerating}
                        onClick={handleRegenerateAll}
                        sx={{ ml: 'auto', fontFamily: "'Poppins', sans-serif", textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                    >
                        {regenerating ? 'Regenerating…' : 'Regenerate All Claims'}
                    </Button>
                )}
            </Box>
            <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', mb: 1.5, fontFamily: "'Poppins', sans-serif" }}>
                Workflow: click a Total Incentive row to record money received from Astern. Click an agent's Incentive row to pay that agent (once Received from Astern). To batch-pay, generate slips on the Slips page (Incentive tab) and approve them there.
            </Typography>

            <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                <MaterialReactTable table={table} />
            </div>
            <IncentiveAsternModal open={!!asternRow} row={asternRow} onClose={() => setAsternRow(null)} onSaved={fetchData} />
            <IncentivePayoutModal open={!!payoutRow} row={payoutRow} onClose={() => setPayoutRow(null)} onSaved={fetchData} />

            <Dialog open={!!caseChildren} onClose={() => setCaseChildren(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
                <DialogTitle sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '1rem' }}>
                    {caseChildren?.agentName} — {caseChildren?.contribution} cases → {handlePriceWithRM(caseChildren?.amount ?? 0)}
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: '0.78rem', color: '#64748B', mb: 1, fontFamily: "'Poppins', sans-serif" }}>
                        {caseChildren?.campaignName} · {caseChildren?.rewardLabel} · contributing sales:
                    </Typography>
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontFamily: "'Poppins', sans-serif" }}>
                        <thead>
                            <tr style={{ background: '#F8FAFC', textAlign: 'left' }}>
                                <th style={{ padding: '6px 8px', fontSize: '0.68rem', color: '#64748B', textTransform: 'uppercase' }}>Sales #</th>
                                <th style={{ padding: '6px 8px', fontSize: '0.68rem', color: '#64748B', textTransform: 'uppercase' }}>Customer</th>
                                <th style={{ padding: '6px 8px', fontSize: '0.68rem', color: '#64748B', textTransform: 'uppercase', textAlign: 'right' }}>Cases</th>
                                <th style={{ padding: '6px 8px', fontSize: '0.68rem', color: '#64748B', textTransform: 'uppercase', textAlign: 'right' }}>Comm %</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(caseChildren?.children ?? []).map((c, i) => (
                                <tr key={i} style={{ borderTop: '1px solid #F1F5F9' }}>
                                    <td style={{ padding: '6px 8px', fontSize: '0.8rem', color: '#374151' }}>{c.salesId}</td>
                                    <td style={{ padding: '6px 8px', fontSize: '0.8rem', color: '#374151' }}>{c.customerName || '—'}</td>
                                    <td style={{ padding: '6px 8px', fontSize: '0.8rem', color: '#374151', textAlign: 'right' }}>{Number(c.contribution ?? 0).toFixed(1)}</td>
                                    <td style={{ padding: '6px 8px', fontSize: '0.8rem', color: '#374151', textAlign: 'right' }}>{c.commProgressPercent != null ? `${c.commProgressPercent}%` : '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setCaseChildren(null)} sx={{ fontFamily: "'Poppins', sans-serif", textTransform: 'none', color: '#64748B' }}>Close</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
